<template>
  <div>
    <public-announcement :is-fixed="true" />
    <invitation-notifications v-if="$store.state.currentUser.id" />
    <edu-yearly-notification v-if="$store.state.currentUser.id" />
    <div class="billing">
      <OnboardingV3BackArrow />
      <page-breadcrumb
        :category="$i18nPresenter.t('account')"
        :text="breadCrumbText"
      />

      <div class="billing-content">
        <aha-section>
          <template #header>
            <h5>{{ $i18nPresenter.t('Billing information') }}</h5>
          </template>
          <template #body>
            <table class="w-full mb-4">
              <tbody>
                <tr>
                  <td class="table-plan-property">
                    <i class="mr-1 fas fa-user fa-fw" />
                    {{ $i18nPresenter.t('Name:') }}
                  </td>
                  <td class="py-2">
                    {{ userName }}
                  </td>
                </tr>
                <tr>
                  <td class="table-plan-property">
                    <i class="mr-1 fas fa-address-card fa-fw" />
                    {{ $i18nPresenter.t('Billing address:') }}
                  </td>
                  <td class="py-2">
                    <div
                      class="table-plan-property__address"
                      v-if="$store.state.currentUser.address"
                    >
                      <PreLine :text="$store.state.currentUser.address" />
                    </div>
                  </td>
                </tr>
                <tr v-if="$store.state.currentUser.stripeCustomerId && $store.state.currentUser.currency !== 'eur'">
                  <td class="table-plan-property">
                    <i class="mr-1 far fa-credit-card fa-fw" />
                    {{ $i18nPresenter.t('Payment method:') }}
                  </td>
                  <td class="py-2">
                    <div class="col" v-if="data.card">
                      <strong class="uppercase">{{ data.card.brand }}</strong> •••• •••• ••••
                      <strong>{{ data.card.last4 }}</strong>
                      <br>
                      <span
                        v-if="data.subscription"
                        class="small-90"
                      >
                        <span
                          class="text-link--muted"
                          @click="showPopupUpdateCardInfo()"
                        >{{ $i18nPresenter.t('Update') }}</span> / </span>
                      <span
                        class="text-link--muted small-90"
                        @click="removeCardInfo()"
                      >
                        {{ $i18nPresenter.t('Remove') }}</span>
                    </div>
                    <div class="col" v-else>
                      <strong>{{ $i18nPresenter.t('None') }}</strong>
                      <br>
                      <span
                        class="text-link--muted small-90"
                        @click="showPopupUpdateCardInfo()"
                      >
                        {{ $i18nPresenter.t('Add new card') }}</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            <aha-button>
              <router-link class="hover:text-base-0 text-base-0" to="/apps/user-profile">
                {{ $i18nPresenter.t('Change') }}
              </router-link>
            </aha-button>
          </template>
        </aha-section>

        <aha-section>
          <template #header>
            <h5>{{ $i18nPresenter.t('Payment') }}</h5>
          </template>
          <template #body>
            <div class="flex flex-wrap">
              <div class="w-full">
                <table class="w-full payment-table">
                  <thead>
                    <tr>
                      <th>
                        {{ $i18nPresenter.t('Plan') }}
                      </th>
                      <th>
                        {{ $i18nPresenter.t('Amount') }}
                      </th>
                      <th>
                        {{ $i18nPresenter.t('Payment method') }}
                      </th>
                      <th>
                        {{ $i18nPresenter.t('Date') }}
                      </th>
                      <th>
                        {{ $i18nPresenter.t('Status') }}
                      </th>
                      <th>
                      </th>
                    </tr>
                  </thead>
                  <tbody v-if="billings.length">
                    <tr
                      :key="index"
                      v-for="(item, index) in billings"
                      style="width:100%;"
                      class="table-data-body"
                    >
                      <td data-header="Plan">
                        <aha-plan-badge :plan="item.description" />
                      </td>
                      <td data-header="Amount">
                        <span class="uppercase">
                          <span class="font-bold">
                            {{ amountPaidConvert(item) }}
                          </span>
                          <span>
                            {{ item.currency }}
                          </span>
                        </span>
                      </td>
                      <td data-header="Payment Method">
                        <PaymentMethod :source="item.source" :payment-method="item.paymentMethod" />
                      </td>
                      <td class="billing-date" data-header="Date">
                        <span
                          @click="goToBillingDetail(item.invoiceId)"
                          class="cursor-pointer text-primary-90"
                        >
                          {{ item.paymentDate | dateBill }}
                        </span>
                      </td>
                      <td data-header="Status">
                        <span
                          class="uppercase"
                          :class="{
                            'text-green-50': item.status === 'paid',
                            'text-base-60': item.status !== 'paid',
                          }"
                        >
                          {{ item.status }}
                        </span>
                      </td>
                      <td data-header="Invoice">
                        <span
                          @click="goToBillingDetail(item.invoiceId)"
                          class="cursor-pointer text-primary-90"
                        >
                          {{ $i18nPresenter.t('Invoice') }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div
                  class="flex justify-center"
                  v-if="areLoadingBillings"
                >
                  <aha-spinner />
                </div>

                <div
                  v-if="!areLoadingBillings"
                >
                  <h5
                    class="font-medium text-center"
                    style="line-height: 35px;"
                  >
                    <span v-if="!billings.length && !hasError">
                      {{ $i18nPresenter.t('You haven’t made any payment.') }}
                    </span>
                    <span v-if="hasError" class="text-red-100">
                      {{ $i18nPresenter.t('Something went wrong. Please try again.') }}
                    </span>
                  </h5>
                </div>
              </div>
            </div>
          </template>
        </aha-section>
      </div>

      <ConfirmModal
        :data="dataConfirm"
        @handleModalConfirmOk="handleModalConfirmOk"
        v-if="isConfirmCancel"
      />

      <modal-remove-card-info
        @handleRemoveCardInfo="handleRemoveCardInfo"
        @handleCancelRemoveCardInfo="handleCancelRemoveCardInfo"
        v-if="isRemoveCardInfo"
      />

      <modal-update-card-info
        @handleCloseUpdateCardInfo="handleCloseUpdateCardInfo"
        v-if="showUpdateCardInfo"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Config from '@/config'
import Util from '@/util'

// UI
import PaymentMethod from '@/components-v2/billing/PaymentMethod.vue'
import ConfirmModal from '@/components/ConfirmModal.vue'
import PublicAnnouncement from '@/components-v2/common/global-notification/PublicAnnouncement.vue'
import InvitationNotifications from '@/components-v2/common/global-notification/InvitationNotifications.vue'
import EduYearlyNotification from '@/components-v2/common/global-notification/EduYearlyNotification.vue'
import PreLine from '@/components/UI/PreLine'
import ModalRemoveCardInfo from '../modals/RemoveCardInfo.vue'
import ModalUpdateCardInfo from '../modals/UpdateCardInfo.vue'
import PageBreadcrumb from '../common/page-breadcrumb/PageBreadcrumb.vue'
import OnboardingV3BackArrow from '@/components-v2/common/onboarding/OnboardingV3BackArrow.vue'
import storageKeys from '@/constant/storageKeys'

export default {
  components: {
    PaymentMethod,
    ConfirmModal,
    ModalRemoveCardInfo,
    ModalUpdateCardInfo,
    PageBreadcrumb,
    InvitationNotifications,
    EduYearlyNotification,
    PreLine,
    PublicAnnouncement,
    OnboardingV3BackArrow,
  },
  data() {
    return {
      data: {},
      billings: [],
      areLoadingBillings: false,
      hasErrorOnLoadingPresenterPlan: false,
      isPresenterPlanLoading: false,
      hasError: false,
      isRemoveCardInfo: false,
      showUpdateCardInfo: false,
      isConfirmCancel: false,
      dataConfirm: {},
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
    }),
    userName() {
      return this.$store.state.currentUser.firstName ?
        `${this.$store.state.currentUser.firstName} ${this.$store.state.currentUser.lastName}` : ''
    },
    breadCrumbText() {
      return this.$i18nPresenter.t('Payment')
    },
  },
  mounted() {
    this.loadReceiptList()
    this.getMyPlan()
  },
  methods: {
    roundNumber: Util.roundNumber,

    amountPaidConvert(item) {
      if (item.currency === 'eur') {
        return this.roundNumber(item.amountPaidRaw)
      }
      return this.roundNumber(item.amountPaid)
    },

    goToBillingDetail(id) {
      const params = { id }
      this.$router.push({ name: 'BillingDetail', params })
    },
    loadReceiptList() {
      this.areLoadingBillings = true
      this.$http.get(Config.api.invoice)
        .then(
          (res) => {
            this.billings = res.body.invoices
            this.areLoadingBillings = false
          },
          () => {
            this.areLoadingBillings = false
            this.hasError = true
          },
        )
    },

    async getMyPlan() {
      this.isPresenterPlanLoading = true
      this.hasErrorOnLoadingPresenterPlan = false

      try {
        const res = await this.$http.get(Config.api.myPlan)
        this.data = res.body
      } catch (error) {
        this.hasErrorOnLoadingPresenterPlan = true
        Util.mixpanelTracking('User Plan', 'Get user plan', 'Get user plan failed')
        throw error
      } finally {
        this.isPresenterPlanLoading = false
      }
    },

    handleModalConfirmOk() {
      this.isConfirmCancel = false
      this.isCancelledSubscription = false
    },

    removeCardInfo() {
      Util.mixpanelTracking(
        'User Plan',
        'User remove card info',
        'User click on remove card info',
      )
      const oldPlan = Util.isSubscribleOldPlan(this.currentUser.plan)
      if (!oldPlan) {
        localStorage.setItem(storageKeys.IS_REMOVE_CARD_PURPOSE, true)
        setTimeout(() => {
          this.$router.push({ name: 'UserCancelSub' })
        }, 500)
      } else {
        this.isRemoveCardInfo = true
      }
    },

    async handleRemoveCardInfo() {
      this.isRemoveCardInfo = false
      if (this.data.subscription) {
        this.data.subscription.cancel_at_period_end = true
      }
      this.autoRenewOn = false
      this.data.card = null
      Util.mixpanelTracking(
        'User Plan',
        'User remove card info',
        'User click on OK remove card info',
      )
    },

    handleCancelRemoveCardInfo() {
      this.isRemoveCardInfo = false
      Util.mixpanelTracking(
        'User Plan',
        'User remove card info',
        'User click on cancel remove card info',
      )
    },

    showPopupUpdateCardInfo() {
      Util.mixpanelTracking(
        'User Plan',
        'User update card info',
        'User click on update card info',
      )
      this.showUpdateCardInfo = true
    },

    handleCloseUpdateCardInfo(card) {
      this.isTurningOnAutoRenew = false
      this.showUpdateCardInfo = false
      if (card) {
        this.data.card = card
      }
    },
    getPlanBadgeClass(plan) {
      const lowercasePlan = plan.toLowerCase()
      if (lowercasePlan.includes('medium') || lowercasePlan.includes('plus')) {
        return 'bg-green-50 text-base-0'
      } if (lowercasePlan.includes('small') || lowercasePlan.includes('essential')) {
        return 'bg-yellow-50 text-base-0'
      } if (lowercasePlan.includes('pro') || lowercasePlan.includes('large')) {
        return 'bg-purple-50 text-base-0'
      }
      return 'bg-primary-90 text-base-0'
    },
  },
}
</script>

<style lang="scss" scoped>
.billing {
  @apply p-4 md:px-10 md:py-6 relative;
}
.billing-content {
  @apply flex flex-col justify-center md:w-10/12 md:mx-auto;
}

.table-plan-property {
   @apply py-2 md:w-1/3 align-top font-bold;
  &__address {
    @apply mt-1 overflow-y-auto;
    max-height: 10rem;
  }
}

.table-plan-value {
  @apply py-2;
}

.payment-table {
  @apply border-collapse;
}

.payment-table tbody tr {
  &:nth-child(odd) {
    @apply bg-secondary-10;
    td:first-child {
      @apply rounded-l-md;
    }
    td:last-child {
      @apply rounded-r-md;

    }
  }
}

.payment-table td,
.payment-table th {
  @apply py-5 px-4;
}

.payment-table th {
  @apply text-left pt-0;

}

@media screen and (max-width:1024px) {
  .payment-table {
    @apply mx-auto w-full;
    border-spacing: 0;
  }
  .payment-table thead {
    @apply absolute p-0 border-none overflow-hidden;
    clip: rect(1px, 1px, 1px, 1px);
    height: 1px;
    width: 1px;
  }
  .payment-table tbody,
  .payment-table tr,
  .payment-table th,
  .payment-table td {
    @apply block text-left whitespace-normal;
  }
  .payment-table tr {
    @apply p-4 rounded-lg;
  }
  .payment-table tr td,
  .payment-table tr th {
    @apply p-2 overflow-hidden relative align-top w-full whitespace-normal;
  }

  .payment-table td[data-header]:before {
    @apply block float-left w-1/2 text-secondary-100 text-left font-bold;
    content: attr(data-header);
  }
  .payment-table td[data-header] > * {
    @apply block w-1/2 float-right clear-right pl-4 mt-0;
  }
  .payment-table td[data-header]:after {
    @apply absolute top-0 left-0 w-1/2 block;
    content: "";
    padding-bottom: 200%;
  }
}

</style>
